import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../api/axios';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('ca_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(false);

  const persist = (data) => {
    localStorage.setItem('ca_token', data.token);
    localStorage.setItem('ca_user', JSON.stringify(data));
    setUser(data);
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      persist(data);
      return data;
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', payload);
      persist(data);
      return data;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('ca_token');
    localStorage.removeItem('ca_user');
    setUser(null);
  };

  useEffect(() => {
    const token = localStorage.getItem('ca_token');
    if (!token) return;

    api.get('/auth/me')
      .then(({ data }) => {
        const refreshed = { ...data, token };
        localStorage.setItem('ca_user', JSON.stringify(refreshed));
        setUser(refreshed);
      })
      .catch(() => logout());
  }, []);

  const refreshUser = async () => {
    const token = localStorage.getItem('ca_token');
    if (!token) return null;
    try {
      const { data } = await api.get('/auth/me');
      const refreshed = { ...data, token };
      localStorage.setItem('ca_user', JSON.stringify(refreshed));
      setUser(refreshed);
      return refreshed;
    } catch {
      return null;
    }
  };

  // Permission helper for admin UI: hasPerm('products','create')
  const hasPerm = (moduleKey, action) => {
    if (!user || user.accountType !== 'admin') return false;
    if (user.role?.isSystem) return true;
    const modulePerm = (user.role?.permissions || []).find((p) => p.module === moduleKey);
    return !!modulePerm?.actions?.includes(action);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, hasPerm, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
